import * as d3 from 'd3';
import addAxisHover from '../../lib/addAxisHover';
import chart_bar_scheme from '../../lib/chart_bar_scheme';

export default {
  constructor() {
    this.width = null;
  },

  async create(
    el,
    data,
    {
      primaryColour = '#666',
      highlightColour = '#333',
      axisLabels = {},
      axisHoverText = {},
      suffix = null,
      animate = true,
    },
  ) {
    const outerWidth = el.getBoundingClientRect().width;
    const barHeight = 75;
    const barMargin = 20;
    const margin = { top: 5, right: 100, bottom: 30, left: 15 };

    // 1. Chart height based on data length
    let scaledHeight = data.length * barHeight;
    let height = scaledHeight - margin.top - margin.bottom;

    // 2. Check for segment labeling
    let segmentLabel = data.some((d) => d.ques_label_when_segment);

    // 3. Calculate unique segment count
    let uniqueCount = 0;
    if (segmentLabel) {
      const allSegmentLabels = [...new Set(data.map((d) => d.segment_value))];
      uniqueCount = allSegmentLabels.filter((v) => typeof v === 'string' && v.trim() !== '').length;
    }

    // 4. Insert blank rows to separate segment groups
    if (segmentLabel) {
      let dataCount = 0;
      for (let i = 0; i < data.length; i++) {
        if (dataCount === uniqueCount) {
          const dummy = {
            value: 0,
            label: `hideThisElement${i}`,
            segment_value: '',
            skipValueLabel: true,
          };
          data.splice(i, 0, dummy);
          dataCount = 0;
        } else {
          dataCount++;
        }
      }
    }

    // 5. Wait for font loading (to measure label width)
    if (document.fonts && document.fonts.ready) {
      await Promise.race([
        document.fonts.ready,
        new Promise((resolve) => setTimeout(resolve, 2000)),
      ]);
    }

    // 6. Compute dynamic label widths
    const maxLabelLen = 20;
    const labelWidths = this.getLabelWidths({
      el,
      data,
      maxLabelLen,
      axisLabels,
    });

    margin.left += labelWidths;
    const width = outerWidth - margin.left - margin.right;

    // 7. Create SVG
    const svg = d3
      .select(el)
      .attr('width', outerWidth)
      .attr('height', scaledHeight + margin.top + margin.bottom)
      .attr('viewBox', `0 0 ${outerWidth} ${scaledHeight}`);

    const chart = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // 8. Set X-axis scale (supporting negative, zero, positive)
    const values = data.map((d) => parseFloat(d.value));
    const minVal = Math.min(0, d3.min(values));
    const maxVal = d3.max(values) === 0 ? 1 : d3.max(values);

    const x = d3.scaleLinear().domain([minVal, maxVal]).range([0, width]).nice();

    // 9. Y-axis scale
    const y = d3
      .scaleBand()
      .domain(data.map((d) => d.label))
      .range([0, height])
      .padding(0.2);

    const barWidth = height / data.length;

    const bar = chart
      .selectAll('g')
      .data(data)
      .enter()
      .append('g')
      .attr('transform', (d, i) => 'translate(0, ' + i * barWidth + ')');

    let barCount = 0;
    bar.each(function (d) {
      let el = d3.select(this);
      if ('link' in d) {
        el = el.append('svg:a').attr('href', d.link);
      }
      let useThisColour = primaryColour;
      if (d.theme_color) {
        useThisColour = d.theme_color;
      }
      if (segmentLabel) {
        // Assign a color based on this
        useThisColour = chart_bar_scheme(barCount);
      }
      if (d.label.includes('Strongly negative count')) {
        useThisColour = 'var(--very-negative-soft)';
      } else if (d.label.includes('Negative count')) {
        useThisColour = 'var(--somewhat-negative-soft)';
      } else if (d.label.includes('Neutral count')) {
        useThisColour = 'var(--neutral-soft)';
      } else if (d.label.includes('Positive count')) {
        useThisColour = 'var(--somewhat-positive-soft)';
      } else if (d.label.includes('Strongly positive count')) {
        useThisColour = 'var(--very-positive-soft)';
      }
      const rect = el
        .append('rect')
        .attr('class', 'bar')
        .attr('y', barMargin / 2)
        .attr('x', 0)
        .attr('height', barWidth - barMargin)
        .attr('fill', (d) => {
          if ('highlight' in d && d.highlight) {
            return highlightColour;
          }
          return useThisColour;
        });

      if (animate) {
        rect
          .transition()
          .duration(800)
          .attr('width', (d) => x(d.value));
      } else {
        rect.attr('width', (d) => x(d.value));
      }

      // used to ensure bars repeat colors if under max of ten
      if (barCount == uniqueCount) {
        barCount = 0;
      } else {
        barCount = barCount + 1;
      }
    });

    const text = bar
      .append('text')
      .attr('y', (barWidth + barMargin) / 2)
      .attr('x', (d) => x(d.value) + 2)
      .attr('dy', '-0.3em')
      .attr('class', 'chart__label')
      .attr('text-anchor', 'left')
      .text((d) => (d.value || !d.skipValueLabel ? d.value + (suffix ? suffix : '') : ''));

    if (segmentLabel) {
      // const ySegmentLabel = bar
      //   .append('text')
      //   .attr('x', 0)
      //   .attr('y', barMargin / 2)
      //   .attr('dy', '-0.5em')
      //   .attr('dx', '0.5em')
      //   .attr('text-anchor', 'left')
      //   .attr('font-family', 'Open Sans Condensed')
      //   .text((d) => d.ques_label_when_segment);
    }

    if (animate) {
      text.style('opacity', 0).transition().delay(800).style('opacity', 1);
    }

    // Axis
    const yAxis = d3.axisLeft(y).tickFormat((d) => d, maxLabelLen);

    let yAxisHover = null;

    const leftAxis = chart
      .append('g')
      .call(yAxis)
      .call((g) => {
        g.attr('font-size', null);
        g.attr('font-family', null);
      });

    if (axisHoverText.y) {
      leftAxis
        .on('mouseover', function () {
          const coords = d3.pointer(this);
          yAxisHover
            .attr('visibility', 'visible')
            .attr('transform', 'translate(0, ' + (coords[1] - 80) + ')');
        })
        .on('mouseout', () => {
          yAxisHover.attr('visibility', 'hidden');
        });

      yAxisHover = addAxisHover(leftAxis, axisHoverText.y);
    }

    leftAxis.selectAll('.tick').each(function (d) {
      const p = data.find((i) => i.label && i.label == d);
      const tick = d3.select(this);
      const text = tick.select('text');
      const textNode = text.node();

      if ('labelLink' in p) {
        const link = tick.append('svg:a').attr('href', p.labelLink);
        link.append(() => textNode);
      }

      if (textNode.textContent.length > maxLabelLen) {
        const fullText = textNode.textContent;
        textNode.textContent = trimLabel(textNode.textContent, maxLabelLen);

        const flyout = tick.append('g').attr('visibility', 'hidden');

        const flyoutText = flyout
          .append('text')
          .text(fullText)
          .attr('x', textNode.getBBox().x)
          .attr('text-anchor', 'start')
          .attr('dy', '0.32em')
          .attr('fill', 'currentColor');

        const flyoutTextNode = flyoutText.node();

        flyout
          .insert('rect', ':first-child')
          .attr('x', flyoutTextNode.getBBox().x - 5)
          .attr('y', flyoutTextNode.getBBox().y - 5)
          .attr('text-anchor', 'start')
          .attr('width', flyoutTextNode.getBBox().width + 10)
          .attr('height', flyoutTextNode.getBBox().height + 10)
          .attr('fill', 'white')
          .attr('rx', 5);

        tick.on('mouseover', function () {
          flyout.attr('visibility', 'visible');
        });
        tick.on('mouseout', function () {
          flyout.attr('visibility', 'hidden');
        });

        if ('labelLink' in p) {
          const link = tick.append('svg:a').attr('href', p.labelLink);
          link.append(() => flyout.node());
        }
      }

      // for optimization can skip above?
      if (segmentLabel) {
        textNode.textContent = p.segment_value;
      }
    });

    if (axisLabels.y && !segmentLabel) {
      const yAxisLabel = chart
        .append('text')
        .attr('x', 0)
        .attr('y', height / 2)
        .attr('dy', -margin.left)
        .attr('dominant-baseline', 'hanging')
        .attr('transform', 'rotate(-90, 0, ' + height / 2 + ')')
        .attr('text-anchor', 'middle')
        .attr('class', 'chart__axis-label')
        .text(axisLabels.y);

      if (axisHoverText.y) {
        yAxisLabel
          .on('mouseover', function () {
            yAxisHover
              .attr('visibility', 'visible')
              .attr('transform', 'translate(0, ' + height / 2 + ')');
          })
          .on('mouseout', () => {
            yAxisHover.attr('visibility', 'hidden');
          });
      }
    }

    const xAxis = d3.axisBottom(x);
    if (!axisHoverText.x) {
      xAxis.ticks(5, '~s');
    }

    let xAxisHover = null;

    const xAxisG = chart
      .append('g')
      .attr('transform', 'translate(0, ' + height + ')')
      .call(xAxis)
      .call((g) => {
        g.attr('font-size', null);
        g.attr('font-family', null);
      });

    if (axisHoverText.x) {
      xAxisG
        .on('mouseover', () => {
          const bbox = xAxisHover.node().getBBox();
          const centerPos = width / 2 - bbox.width / 2;
          xAxisHover
            .attr('transform', 'translate(' + centerPos + ', ' + -bbox.height + ')')
            .attr('visibility', 'visible');
        })
        .on('mouseout', () => {
          xAxisHover.attr('visibility', 'hidden');
        });
    }

    if (axisLabels.x) {
      const xAxisLabel = chart
        .append('text')
        .attr('x', width / 2)
        .attr('y', height)
        .attr('dy', margin.bottom)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'hanging')
        .attr('class', 'chart__axis-label')
        .text(axisLabels.x);

      if (axisHoverText.x) {
        xAxisLabel
          .on('mouseover', function () {
            const bbox = xAxisHover.node().getBBox();
            const centerPos = width / 2 - bbox.width / 2;
            xAxisHover
              .attr('transform', 'translate(' + centerPos + ', ' + -bbox.height + ')')
              .attr('visibility', 'visible');
          })
          .on('mouseout', () => {
            xAxisHover.attr('visibility', 'hidden');
          });
      }
    }

    if (axisHoverText.x) {
      xAxisHover = addAxisHover(xAxisG, axisHoverText.x);
    }
  },

  empty(el) {
    const parent = d3.select(el);
    parent.selectAll('*').remove();
  },

  getLabelWidths({ el, data, maxLabelLen, axisLabels }) {
    let labelWidths = data.map((i) => {
      const textToUse = i.label ? i.label.substring(0, maxLabelLen) : '';
      const text = d3.select(el).append('text').text(textToUse);

      const width = text.node().getBoundingClientRect().width;
      text.remove();
      return width;
    });

    let axisLabelWidth = 0;
    if (axisLabels.y) {
      axisLabelWidth = (() => {
        const text = d3.select(el).append('text').text(axisLabels.y);

        const height = text.node().getBoundingClientRect().height;
        text.remove();
        return height;
      })();
    }

    return Math.max(...labelWidths) + axisLabelWidth;
  },

  applyStyles(el, styles) {
    const selection = d3.select(el);
    Object.keys(styles).forEach((attr) => {
      selection.attr(attr, styles[attr]);
    });
  },
};

function trimLabel(label, maxLabelLen) {
  if (label.length < maxLabelLen) {
    return label;
  } else {
    return label.substring(0, maxLabelLen - 3) + '...';
  }
}
